import type { FeatureFlagVariant } from '@/lib/feature-flags/types';

/**
 * Where the trial-start arm is recorded and how to read it back.
 *
 * This lives in `lib` rather than beside the portfolio feature because the arm is not a portfolio
 * concern once it is stamped: `useAnalytics` puts it on every event in the app, since three of the
 * five pre-registered guardrails (externship enrolls, week-1 submission, refunds) sit outside the
 * portfolio funnel and still have to be read per arm.
 *
 * Assignment — who gets an arm, when, and from which flag — stays in
 * `features/portfolio/_utils/portfolio-trial-arm.ts`. This module only reads the stamp.
 */

/** Clerk `unsafeMetadata`, same store as the nudge latches — device-independent and sticky. */
export const PORTFOLIO_TRIAL_ARM_KEY = 'portfolio_trial_start_arm';
export const PORTFOLIO_TRIAL_ARM_ASSIGNED_AT_KEY = 'portfolio_trial_start_arm_assigned_at';

export type PortfolioTrialArm = 'control' | 'experiment';

const PORTFOLIO_TRIAL_ARMS: readonly FeatureFlagVariant[] = ['control', 'experiment'];

export function isPortfolioTrialArm(value: unknown): value is PortfolioTrialArm {
  return typeof value === 'string' && PORTFOLIO_TRIAL_ARMS.includes(value as FeatureFlagVariant);
}

/** The stamped arm, or null when this user has never been assigned. */
export function readPortfolioTrialArm(
  unsafeMetadata: Record<string, unknown> | null | undefined,
): PortfolioTrialArm | null {
  const stamped = unsafeMetadata?.[PORTFOLIO_TRIAL_ARM_KEY];

  return isPortfolioTrialArm(stamped) ? stamped : null;
}

/** When the stamp was written, for cohorting trials by enrollment date. Absent for older stamps. */
export function readPortfolioTrialArmAssignedAt(
  unsafeMetadata: Record<string, unknown> | null | undefined,
): string | null {
  const assignedAt = unsafeMetadata?.[PORTFOLIO_TRIAL_ARM_ASSIGNED_AT_KEY];

  return typeof assignedAt === 'string' ? assignedAt : null;
}
